import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Input, Label } from '../Shared';
import TextareaAutosize from 'react-textarea-autosize';
import { calculateEmbossCost } from '../Functions.js/Embossing';
import { calculateFoilingCost } from '../Functions.js/Foiling';

const presses = [
  { name: 'Large Miller', minWidth: 26, minLength: 20, maxWidth: 40, maxLength: 28, piecesPerHour: 1200, perHourCost: 70, mrCost: 75 },
  { name: 'Small Miller', minWidth: 20, minLength: 13, maxWidth: 28, maxLength: 20, piecesPerHour: 1800, perHourCost: 55, mrCost: 55 },
  { name: 'Thompson', minWidth: 12, minLength: 9, maxWidth: 40, maxLength: 28, piecesPerHour: 500, perHourCost: 100, mrCost: 55 },
  { name: 'Saroglia', minWidth: 12, minLength: 9, maxWidth: 28, maxLength: 20, piecesPerHour: 1200, perHourCost: 55, mrCost: 55 },
  { name: 'Yawa', minWidth: 13, minLength: 10, maxWidth: 28, maxLength: 20, piecesPerHour: 1200, perHourCost: 55, mrCost: 55 },
  { name: 'Large Kluge', minWidth: 15, minLength: 11, maxWidth: 21, maxLength: 13, piecesPerHour: 1500, perHourCost: 55, mrCost: 55 },
  { name: 'Small Kluge', minWidth: 11, minLength: 8.5, maxWidth: 18, maxLength: 12, piecesPerHour: 1800, perHourCost: 55, mrCost: 55 },
];

const NumInput = React.forwardRef(({ value, onChange, placeholder, ...props }, ref) => {
  const formatForDisplay = (val) => (val == null || val === '') ? '' : String(Number(val));

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === '') return onChange(null);
    const num = Number(raw);
    if (!isNaN(num)) onChange(num);
  };

  return (
    <Input
      ref={ref}
      type="number"
      value={formatForDisplay(value)}
      onChange={handleChange}
      placeholder={placeholder || "0"}
      {...props}
    />
  );
});

const getBestPress = (rawW, rawL, needsSpecial) => {
  const w = Number(rawW) || 0;
  const l = Number(rawL) || 0;
  if (!w || !l) return null;
  const orientations = [{ grip: w, feed: l }, { grip: l, feed: w }];
  let bestPress = null;
  let highestSpeed = 0;
  for (const { grip, feed } of orientations) {
    const validPresses = presses.filter(p =>
      grip >= p.minWidth && grip <= p.maxWidth &&
      feed >= p.minLength && feed <= p.maxLength &&
      !(needsSpecial && p.name.includes('Miller'))
    );
    if (validPresses.length > 0) {
      const fastest = validPresses.reduce((a, b) => a.piecesPerHour > b.piecesPerHour ? a : b);
      if (fastest.piecesPerHour > highestSpeed) {
        highestSpeed = fastest.piecesPerHour;
        bestPress = fastest.name;
      }
    }
  }
  return bestPress;
};

const getBestPressObject = (w, l) => {
  if (!w || !l) return null;
  const orientations = [{ grip: w, feed: l }, { grip: l, feed: w }];
  let best = null;
  let highestSpeed = 0;
  for (const { grip, feed } of orientations) {
    const valid = presses.filter(p =>
      grip >= p.minWidth && grip <= p.maxWidth &&
      feed >= p.minLength && feed <= p.maxLength &&
      !p.name.includes('Miller')
    );
    if (valid.length > 0) {
      const fastest = valid.reduce((a, b) => a.piecesPerHour > b.piecesPerHour ? a : b);
      if (fastest.piecesPerHour > highestSpeed) {
        highestSpeed = fastest.piecesPerHour;
        best = fastest;
      }
    }
  }
  return best;
};

const Necc = ({ closeMini, setEstitems, estitems = {}, editingItem }) => {
  const [width, setWidth] = useState(editingItem?.width || '');
  const [length, setLength] = useState(editingItem?.length || '');
  const [quantities, setQuantities] = useState(
    editingItem?.quantities?.map(q => ({
      ...q,
      roundedTotal: q.roundedTotal !== undefined ? q.roundedTotal : undefined
    })) || []
  );
  
  const [selectedPress, setSelectedPress] = useState(editingItem?.press || '');
  const quanbox = useRef(null);
  const [tapingInches, setTapingInches] = useState(editingItem?.tapingInches || 0);
  const [dieCost, setDieCost] = useState(editingItem?.dieCost ?? 0);
  const [dieMr, setDieMr] = useState(editingItem?.dieMr ?? 0);
  const [additionalCharges, setAdditionalCharges] = useState(editingItem?.additionalCharges ?? 0);
  const [dieCut, setDieCut] = useState(!!editingItem?.dieCut || false);
  const [perf, setPerf] = useState(!!editingItem?.perf);
  const [score, setScore] = useState(!!editingItem?.score);
  const [stripping, setStripping] = useState(!!editingItem?.stripping);
  const [strippingRatePerThousand, setStrippingRatePerThousand] = useState(
    editingItem?.strippingRatePerThousand ?? 6
  );
  const [strippingCount, setStrippingCount] = useState(editingItem?.strippingCount ?? 1);
  const [numUp, setNumUp] = useState(editingItem?.numUp ?? 1);
  const AUTO_FOLD_RATE = 30;
  const [boxRate, setBoxRate] = useState({
    rate: editingItem?.boxRate?.rate ?? 0,
    override: editingItem?.boxRate?.override ?? null
  });
  const [foldingMode, setFoldingMode] = useState(editingItem?.foldingMode || 'none');
  const [foldingCount, setFoldingCount] = useState(editingItem?.folding ?? 0);
  const [description, setDescription] = useState(editingItem?.description || '');

  const [emboss, setEmboss] = useState(editingItem?.emboss || {
    state: false,
    plates: [{
      id: Date.now(),
      plateWidth: 0, plateLength: 0,
      pressWidth: 0, pressLength: 0,
      platecost: 0, overridePlatecost: null,
      mr: 50, overrideMr: null,
      backer: 59, overrideBacker: null,
      selectedPress: null, pressName: ''
    }]
  });

  const [deboss, setDeboss] = useState(editingItem?.deboss || {
    state: false,
    plates: [{
      id: Date.now(),
      plateWidth: 0, plateLength: 0,
      pressWidth: 0, pressLength: 0,
      platecost: 0, overridePlatecost: null,
      mr: 50, overrideMr: null,
      backer: 59, overrideBacker: null,
      selectedPress: null, pressName: ''
    }]
  });

  const [foil, setFoil] = useState(editingItem?.foil || {
    state: false,
    mr: 50,
    plates: [{
      id: Date.now(),
      plateWidth: 0, plateLength: 0,
      pressWidth: 0, pressLength: 0,
      platecost: 0, overridePlatecost: null,
      color: '', passes: 1,
      foilCost: null, overrideFoilCost: null,
      selectedPress: null, pressName: ''
    }]
  });

  const [matPercent, setMatPercent] = useState(editingItem?.matPercent ?? 1.30);
  const mat = Number(matPercent) || 1.30;

  const [labPercent, setLabPercent] = useState(editingItem?.labPercent ?? 1.15);
  const lab = Number(labPercent) || 1.15;

  const needsSpecialFinish = emboss.state || deboss.state || foil.state;
  const bestPress = useMemo(() => getBestPress(width, length, needsSpecialFinish), [width, length, needsSpecialFinish]);

  const isLargeSheet = Number(width) > 11 || Number(length) > 17;
  const runRate = isLargeSheet ? 75 : 50;

  const getExtraRunCost = (quantity, plateCount = 1) => (quantity / 1000) * runRate * plateCount;

  // LIVE RECALCULATION OF ALL QUANTITY TOTALS + BREAKDOWN VALUES
  useEffect(() => {
    if (quantities.length === 0) return;

    setQuantities(prev => prev.map(q => {
      const qty = q.quantity;
      const totalPieces = qty * (numUp || 1);

      const calculateRun = (state) => state?.state
        ? state.plates.reduce((sum, plate) => {
            if (!plate.selectedPress || !plate.pressWidth || !plate.pressLength) {
              return sum + getExtraRunCost(qty, 1);
            }
            const hours = qty / plate.selectedPress.piecesPerHour;
            const passes = (state === foil) ? (plate.passes || 1) : 1;
            return sum + (hours * plate.selectedPress.perHourCost * passes * lab);
          }, 0)
        : 0;

      const embossRun = calculateRun(emboss);
      const debossRun = calculateRun(deboss);
      const foilRun = calculateRun(foil);

      const foilMaterialCost = foil.state
        ? foil.plates.reduce((sum, p) => {
            if (!p.plateWidth || !p.plateLength) return sum;
            const result = calculateFoilingCost({ quantity: qty }, p.plateLength, p.plateWidth);
            return sum + (p.overrideFoilCost ?? result?.foil?.foilcost ?? 0);
          }, 0)
        : 0;

      const foilPlateAndMrCost = foil.state
        ? foil.plates.reduce((s, p) => s + (p.overridePlatecost ?? p.platecost), 0) +
          (foil.mr * lab * foil.plates.reduce((s, p) => s + (p.passes || 1), 0))
        : 0;

      const embossSetup = emboss.state
        ? emboss.plates.reduce((s, p) =>
            s + ((p.overridePlatecost ?? p.platecost) * mat) +
                ((p.overrideBacker ?? p.backer) * mat) +
                ((p.overrideMr ?? p.mr) * lab), 0)
        : 0;

      const debossSetup = deboss.state
        ? deboss.plates.reduce((s, p) =>
            s + ((p.overridePlatecost ?? p.platecost) * mat) +
                ((p.overrideBacker ?? p.backer) * mat) +
                ((p.overrideMr ?? p.mr) * lab), 0)
        : 0;

      const dieTotal = dieCost * mat;
      const mrTotal = dieMr * lab;
      const additionalTotal = Number(additionalCharges) || 0;

      const stripTotal = stripping
        ? ((qty * numUp) / 1000) * strippingRatePerThousand * (strippingCount || 1) * lab
        : 0;

      const foldingCost = foldingMode !== 'none' && foldingCount > 0
        ? ((qty * numUp) / 1000) * (foldingMode === 'hand' ? 20 : AUTO_FOLD_RATE) * foldingCount
        : 0;

      const tapingCost = tapingInches > 0 ? ((qty * numUp) / 1000) * 6.41 * tapingInches : 0;
      const tapingRunCost = tapingInches > 0 ? Math.ceil(totalPieces / 300) * 42 : 0;

      const boxesPerThousand = Math.ceil(totalPieces / 1000);
      const ratePerThousand = boxRate.override !== null ? boxRate.override : boxRate.rate;
      const boxesCost = boxesPerThousand * ratePerThousand * mat;

      const rawTotal =
        (q.runCost || 0) +
        embossRun + debossRun + foilRun +
        foilPlateAndMrCost + foilMaterialCost +
        embossSetup + debossSetup +
        dieTotal + mrTotal + additionalTotal + stripTotal +
        foldingCost + tapingCost + tapingRunCost + boxesCost;

      const grandTotal = Math.max(200, rawTotal);
      const calculatedRounded = Math.ceil(grandTotal);

      const roundedTotal =
        q.roundedTotal !== undefined && q.roundedTotal !== calculatedRounded
          ? q.roundedTotal
          : calculatedRounded;

      // Store ALL values for render use
      return {
        ...q,
        grandTotal,
        roundedTotal,
        embossRun,
        debossRun,
        foilRun,
        foilMaterialCost,
        foilPlateAndMrCost,
        embossSetup,
        debossSetup,
        dieTotal,
        mrTotal,
        additionalTotal,
        stripTotal,
        foldingCost,
        tapingCost,
        tapingRunCost,
        boxesCost,
      
      };
    }));
  }, [
    width, length, selectedPress,
    dieCut, perf, score, dieCost, dieMr, additionalCharges,
    stripping, strippingRatePerThousand, strippingCount,
    numUp, boxRate.rate, boxRate.override, foldingMode, foldingCount,
    tapingInches, mat, lab,
    emboss.state, deboss.state, foil.state,
    JSON.stringify(emboss.plates),
    JSON.stringify(deboss.plates),
    JSON.stringify(foil.plates),
    quantities.length,
    quantities.map(q => q.quantity).join(',')
  ]);

  useEffect(() => {
    const hasDieWork = dieCut || perf || score;
    if (hasDieWork) {
      setDieCost(prev => prev === 0 ? 10 : prev);
      setDieMr(prev => prev === 0 ? 50 : prev);
    } else {
      setDieCost(0);
      setDieMr(0);
    }
  }, [dieCut, perf, score]);

  useEffect(() => {
    if (!editingItem) return;
    const restorePressObjects = (plates, setter) => {
      setter(prev => ({
        ...prev,
        plates: prev.plates.map(plate => {
          if (!plate.pressName) return plate;
          const fullPress = presses.find(p => p.name === plate.pressName);
          return fullPress ? { ...plate, selectedPress: fullPress } : plate;
        })
      }));
    };
    if (emboss.state) restorePressObjects(emboss.plates, setEmboss);
    if (deboss.state) restorePressObjects(deboss.plates, setDeboss);
    if (foil.state) restorePressObjects(foil.plates, setFoil);
  }, [editingItem]);

  useEffect(() => {
    const updatePress = (setter, state) => {
      if (!state.state) return;
      setter(prev => ({
        ...prev,
        plates: prev.plates.map(plate => {
          if (!plate.pressWidth || !plate.pressLength) return plate;
          const best = getBestPressObject(plate.pressWidth, plate.pressLength);
          if (best && best.name !== plate.pressName) {
            return { ...plate, selectedPress: best, pressName: best.name };
          }
          return plate;
        })
      }));
    };
    updatePress(setEmboss, emboss);
    updatePress(setDeboss, deboss);
    updatePress(setFoil, foil);
  }, [
    emboss.state, deboss.state, foil.state,
    JSON.stringify(emboss.plates.map(p => `${p.pressWidth}-${p.pressLength}`)),
    JSON.stringify(deboss.plates.map(p => `${p.pressWidth}-${p.pressLength}`)),
    JSON.stringify(foil.plates.map(p => `${p.pressWidth}-${p.pressLength}`))
  ]);

  const recalculatePressCosts = () => {
    if (!selectedPress || quantities.length === 0) return;
    const press = presses.find(p => p.name === selectedPress);
    if (!press) return;

    const hasDieWork = dieCut || perf || score;

    setQuantities(prev => prev.map(q => {
      const qty = q.quantity;
      let machineRunCost = 0;
      let hoursNeeded = 0;
      if (hasDieWork) {
        const baseHours = qty / press.piecesPerHour;
        machineRunCost = baseHours * press.perHourCost * lab;
        hoursNeeded = parseFloat(baseHours.toFixed(3));
      }
      return {
        ...q,
        press: selectedPress,
        pressDetails: press,
        hoursNeeded,
        runCost: parseFloat(machineRunCost.toFixed(2)),
        costPerPiece: machineRunCost > 0 ? parseFloat((machineRunCost / qty).toFixed(4)) : 0,
        isDoubleRun: foil.state
      };
    }));
  };

  useEffect(() => {
    if (bestPress && !selectedPress) setSelectedPress(bestPress);
  }, [bestPress, selectedPress]);

  useEffect(() => recalculatePressCosts(), [
    selectedPress,
    foil.state,
    quantities.length,
    dieCut,
    perf,
    score,
    lab
  ]);

  const addquan = (e) => {
    if (e.key !== 'Enter') return;
    const val = e.target.value.trim();
    const qty = parseInt(val, 10);
    if (isNaN(qty) || qty <= 0) return;

    const press = presses.find(p => p.name === selectedPress);
    const hasDieWork = dieCut || perf || score;

    let machineRunCost = 0;
    let hoursNeeded = 0;
    if (hasDieWork && press) {
      const baseHours = qty / press.piecesPerHour;
      machineRunCost = baseHours * press.perHourCost * lab;
      hoursNeeded = parseFloat(baseHours.toFixed(3));
    }

    setQuantities(prev => {
      if (prev.some(x => x.quantity === qty)) return prev;
      return [...prev, {
        quantity: qty,
        press: selectedPress,
        pressDetails: press,
        hoursNeeded,
        runCost: parseFloat(machineRunCost.toFixed(2)),
        costPerPiece: machineRunCost > 0 ? parseFloat((machineRunCost / qty).toFixed(4)) : 0,
        isDoubleRun: foil.state
      }].sort((a, b) => a.quantity - b.quantity);
    });

    e.target.value = '';
    quanbox.current?.focus();
  };

  const removeQuantity = (qty) => setQuantities(prev => prev.filter(q => q.quantity !== qty));

  const handleSubmit = () => {
    if (quantities.length === 0) {
      alert('Please add at least one quantity');
      return;
    }

    const cleanPlates = (plates) => plates.map(p => ({
      plateWidth: p.plateWidth,
      plateLength: p.plateLength,
      pressWidth: p.pressWidth,
      pressLength: p.pressLength,
      platecost: p.overridePlatecost ?? p.platecost,
      mr: p.overrideMr ?? p.mr,
      backer: p.overrideBacker ?? p.backer,
      pressName: p.pressName || null,
      color: p.color,
      passes: p.passes,
      foilCost: p.overrideFoilCost ?? p.foilCost
    }));

    const newItem = {
      id: editingItem?.id || Date.now(),
      type: 'finishing',
      width: Number(width),
      length: Number(length),
      press: selectedPress,
      dieCost: dieCost === 0 ? null : dieCost,
      dieMr: dieMr === 0 ? 0 : dieMr,
      additionalCharges: additionalCharges > 0 ? additionalCharges : undefined,
      stripping,
      strippingRatePerThousand: stripping ? strippingRatePerThousand : undefined,
      strippingCount: stripping ? strippingCount : undefined,
      size: `${width}" x ${length}"`,
      dieCut, perf, score,
      numUp: Number(numUp) || 1,
      boxRate: { rate: boxRate.rate, override: boxRate.override },
      foldingMode,
      folding: Number(foldingCount) || 0,
      tapingInches: Number(tapingInches) || 0,
      description: description.trim() || undefined,
      labPercent: labPercent === 1.15 ? undefined : labPercent,
      matPercent: matPercent === 1.30 ? undefined : matPercent,
      emboss: emboss.state ? { ...emboss, plates: cleanPlates(emboss.plates) } : null,
      deboss: deboss.state ? { ...deboss, plates: cleanPlates(deboss.plates) } : null,
      foil: foil.state ? { ...foil, plates: cleanPlates(foil.plates) } : null,
      quantities: quantities.map(q => ({
        ...q,
        totalPieces: q.quantity * (Number(numUp) || 1),
        roundedTotal: q.roundedTotal
      }))
    };

    setEstitems(prev => {
      const updated = editingItem
        ? prev.necc.map(i => i.id === editingItem.id ? newItem : i)
        : [...(prev.necc || []), newItem];
      return { ...prev, necc: updated };
    });

    closeMini();
  };


  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/70">
      <div className="bg-white rounded-2xl shadow-2xl w-[90%] max-w-7xl mx-4 my-8 max-h-[90vh] overflow-y-auto p-8" style={{padding:'20px'}}>
        <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
          {editingItem ? 'Edit Finishing Item' : 'Add Finishing Item'}
        </h2>

        <div style={{display:'flex', marginBottom: '20px'}}>
          <div>
            <Label>Add Quantity (press Enter)</Label>
            <Input ref={quanbox} width="300px" type="number" placeholder="1000, 2500..." onKeyDown={addquan} />
          </div>
        </div>

        <div className="space-y-8">

          {/* Description */}
          <div>
            <Label>Description / Notes</Label>
            <TextareaAutosize
              minRows={3}
              maxRows={12}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Enter job notes, special instructions, customer requirements, delivery notes, etc..."
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '1rem',
                resize: 'none',
                lineHeight: '1.6',
                backgroundColor: '#f9fafb',
                boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)'
              }}
            />
          </div>

          {/* Options Row */}
          <div className="flex flex-wrap items-center justify-between gap-10" style={{marginBottom:'20px',marginTop:'20px'}}>
            <div className="flex flex-wrap items-center gap-10">
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={dieCut} onChange={e => setDieCut(e.target.checked)} className="w-5 h-5" />
                <span>Die Cut</span>
              </label>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={perf} onChange={e => setPerf(e.target.checked)} className="w-5 h-5" />
                <span>Perf</span>
              </label>
              <label className="flex items-center gap-3">
                <input type="checkbox" checked={score} onChange={e => setScore(e.target.checked)} className="w-5 h-5" />
                <span>Score</span>
              </label>
              <div className="items-center">
                <span className="text-sm font-medium whitespace-nowrap">Num Up:</span><br />
                <NumInput value={numUp} onChange={setNumUp} min="1" width="100px" />
              </div>

              <div className="flex items-center gap-3">
                <div>
                  <select
                    value={foldingMode}
                    onChange={e => setFoldingMode(e.target.value)}
                    className="p-4 border rounded-lg text-lg font-medium mt-2 mb-2"
                    style={{width:"100px"}}
                  >
                    <option value="none">None</option>
                    <option value="hand">Hand</option>
                    <option value="auto">Auto</option>
                  </select>
                  {foldingMode !== 'none' && (
                    <>
                      <br />
                      <NumInput value={foldingCount} onChange={setFoldingCount} min="0" placeholder="0" width="100px" />
                      <span className="text-xs text-gray-500">
                        fold{foldingCount > 1 ? 's' : ''} @ ${foldingMode === 'hand' ? '20' : AUTO_FOLD_RATE}/k
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div>
                <span className="text-sm font-medium whitespace-nowrap">Taping (in):</span><br />
                <NumInput value={tapingInches} onChange={setTapingInches} min="0" step="0.01" placeholder="0" width="80px" />
              </div>

              <div className="flex items-start gap-4">
                <label className="flex items-center gap-3 pt-2">
                  <input type="checkbox" checked={stripping} onChange={e => setStripping(e.target.checked)} className="w-5 h-5" />
                  <span>Stripping</span>
                </label>

                {stripping && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">$</span>
                      <NumInput
                        value={strippingRatePerThousand}
                        onChange={setStrippingRatePerThousand}
                        step="0.01"
                        min="0"
                        width="85px"
                        placeholder="6.00"
                      />
                      <span className="text-sm text-gray-500">/k</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">×</span>
                      <NumInput
                        value={strippingCount}
                        onChange={setStrippingCount}
                        min="1"
                        step="1"
                        width="70px"
                        placeholder="1"
                      />
                      <span className="text-xs text-gray-500">strip{strippingCount !== 1 ? 's' : ''} per piece</span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <span className="text-sm font-medium whitespace-nowrap">Boxes :</span><br />
                <div className="flex items-center gap-2">
                  <strong className={boxRate.override === null ? "text-green-600" : "text-orange-600"}>
                    ${boxRate.override !== null ? boxRate.override.toFixed(2) : boxRate.rate.toFixed(2)}/k
                  </strong>
                  <NumInput
                    value={boxRate.override ?? ''}
                    onChange={v => setBoxRate(prev => ({ ...prev, override: v === '' || v === null ? null : parseFloat(v) }))}
                    step="0.01"
                    placeholder="Override rate"
                    width="90px"
                    className="text-orange-600 font-medium"
                  />
                </div>
              </div>
            </div>
          </div>

          {dieCut || perf || score ? (
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4" style={{marginBottom:'20px'}}>
              <div><Label>Width (inches)</Label><br /><NumInput width="100px" step="0.1" value={width} onChange={setWidth} /></div>
              <div><Label>Length (inches)</Label><br /><NumInput width="100px" step="0.1" value={length} onChange={setLength} /></div>
              <div><Label>Die Cost $</Label><br /><NumInput width="100px" step="0.01" value={dieCost} onChange={setDieCost} placeholder="0.00" /></div>
              <div><span className="text-sm font-medium whitespace-nowrap">MR $</span><br /><NumInput value={dieMr} onChange={setDieMr} width="100px" /></div>
              <div><span className="text-sm font-medium whitespace-nowrap">Additional Charges $</span><br />
                <NumInput 
                  value={additionalCharges} 
                  onChange={setAdditionalCharges} 
                  step="0.01" 
                  min="0" 
                  width="100px" 
                  placeholder="0.00" 
                />
              </div>
              <div style={{marginBottom:'20px'}}>
                <Label>Press Machine</Label>
                <select value={selectedPress} onChange={e => setSelectedPress(e.target.value)}
                  className={`w-full p-4 border-2 rounded-lg text-lg font-medium mt-2 ${selectedPress ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
                  <option value="" disabled>{width && length ? 'Select press...' : 'Enter size first'}</option>
                  {presses.map(p => {
                    const fits = [width, length, length, width].some((v, i) => {
                      const grip = i < 2 ? Number(width) : Number(length);
                      const feed = i < 2 ? Number(length) : Number(width);
                      return grip >= p.minWidth && grip <= p.maxWidth && feed >= p.minLength && feed <= p.maxLength;
                    });
                    const blocked = needsSpecialFinish && p.name.includes('Miller');
                    return (
                      <option key={p.name} value={p.name} disabled={!fits || blocked}>
                        {p.name}{bestPress === p.name ? ' (RECOMMENDED)' : ''}{blocked ? ' (No special finish)' : ''}
                      </option>
                    );
                  })}
                </select>
                {bestPress && !selectedPress && (
                  <p className="text-sm text-green-600 mt-2">Auto-selected: {bestPress}</p>
                )}
              </div>
            </div>
          ) : null}

          {/* EMBOSSING */}
          <div style={{marginBottom:'10px'}}>
            <label className="flex items-center gap-4 text-blue-700 text-xl font-bold">
              <input type="checkbox" checked={emboss.state} onChange={e => setEmboss(p => ({ ...p, state: e.target.checked }))} className="w-6 h-6" />
              Embossing
            </label>
            {emboss.state && (
              <div className="mt-4 p-6 border-2 border-blue-600 rounded-xl bg-blue-50">
                <div className="mb-6 p-5 bg-gradient-to-r from-blue-600 to-blue-800 text-white text-center">
                  <div className="text-xl font-bold">Plate Size = Plate Cost | Press Size = Run Cost</div>
                </div>
                {emboss.plates.map((p, i) => (
                  <div key={p.id} className="mb-6 p-4 bg-white rounded-lg border">
                    <div className="grid grid-cols-2 md:grid-cols-8 gap-4 items-end">
                      <div><Label>Plate W</Label><NumInput step="0.1" value={p.plateWidth} onChange={v => setEmboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, plateWidth: v } : x) }))} /></div>
                      <div><Label>Plate L</Label><NumInput step="0.1" value={p.plateLength} onChange={v => setEmboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, plateLength: v } : x) }))} /></div>
                      <div><Label>Press W</Label><NumInput step="0.1" value={p.pressWidth} onChange={v => setEmboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, pressWidth: v } : x) }))} /></div>
                      <div><Label>Press L</Label><NumInput step="0.1" value={p.pressLength} onChange={v => setEmboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, pressLength: v } : x) }))} /></div>
                      <div className="md:col-span-2">
                        <Label>Press</Label>
                        <select value={p.pressName || ''} onChange={e => {
                          const name = e.target.value;
                          const pressObj = presses.find(pr => pr.name === name) || null;
                          setEmboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, pressName: name, selectedPress: pressObj } : x) }));
                        }} className="w-full p-2 border rounded text-sm">
                          <option value="">Auto-select...</option>
                          {presses.map(pr => {
                            const fits = p.pressWidth && p.pressLength ? (
                              (p.pressWidth >= pr.minWidth && p.pressWidth <= pr.maxWidth && p.pressLength >= pr.minLength && p.pressLength <= pr.maxLength) ||
                              (p.pressLength >= pr.minWidth && p.pressLength <= pr.maxWidth && p.pressWidth >= pr.minLength && p.pressWidth <= pr.maxLength)
                            ) : true;
                            return <option key={pr.name} value={pr.name} disabled={!fits || pr.name.includes('Miller')}>{pr.name} ({pr.piecesPerHour}/h)</option>;
                          })}
                        </select>
                        {p.pressName && <small className="text-green-600 block">{p.pressName}</small>}
                      </div>
                      <div><Label>Auto Plate</Label><strong>${(p.platecost || 0).toFixed(2)}</strong><br />
                        <Input type="number" step="0.01" value={p.overridePlatecost ?? ''} onChange={e => {
                          const val = e.target.value;
                          setEmboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, overridePlatecost: val === '' ? null : parseFloat(val) } : x) }));
                        }} placeholder="Override" />
                      </div>
                      <div><Label>MR $</Label><NumInput step="0.01" value={p.overrideMr ?? p.mr} onChange={v => setEmboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, overrideMr: v === 0 ? null : v } : x) }))} /></div>
                      <div><Label>Backer $</Label>
                        <NumInput
                          step="1"
                          min="0"
                          value={p.overrideBacker ?? p.backer}
                          onChange={v => setEmboss(s => ({
                            ...s,
                            plates: s.plates.map((x, j) => j === i
                              ? { ...x, overrideBacker: v == null ? null : v }
                              : x
                            )
                          }))}
                        />
                      </div>
                      {i > 0 && <button onClick={() => setEmboss(s => ({ ...s, plates: s.plates.filter((_, j) => j !== i) }))} className="text-red-600">Remove</button>}
                    </div>
                  </div>
                ))}
                <button onClick={() => setEmboss(s => ({ ...s, plates: [...s.plates, {
                  id: Date.now(), plateWidth: 0, plateLength: 0, pressWidth: 0, pressLength: 0,
                  platecost: 0, overridePlatecost: null, mr: 50, overrideMr: null, backer: 59, overrideBacker: null,
                  selectedPress: null, pressName: ''
                }] }))} className="text-blue-700 font-bold text-lg">+ Add Plate</button>
              </div>
            )}
          </div>

          {/* DEBOSSING */}
          <div style={{marginBottom:'10px'}}>
            <label className="flex items-center gap-4 text-red-700 text-xl font-bold">
              <input type="checkbox" checked={deboss.state} onChange={e => setDeboss(p => ({ ...p, state: e.target.checked }))} className="w-6 h-6" />
              Debossing
            </label>
            {deboss.state && (
              <div className="mt-4 p-6 border-2 border-red-600 rounded-xl bg-red-50">
                <div className="mb-6 p-5 bg-gradient-to-r from-red-600 to-red-800 text-white text-center">
                  <div className="text-xl font-bold">Plate Size = Plate Cost | Press Size = Run Cost</div>
                </div>
                {deboss.plates.map((p, i) => (
                  <div key={p.id} className="mb-6 p-4 bg-white rounded-lg border">
                    <div className="grid grid-cols-2 md:grid-cols-8 gap-4 items-end">
                      <div><Label>Plate W</Label><NumInput step="0.1" value={p.plateWidth} onChange={v => setDeboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, plateWidth: v } : x) }))} /></div>
                      <div><Label>Plate L</Label><NumInput step="0.1" value={p.plateLength} onChange={v => setDeboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, plateLength: v } : x) }))} /></div>
                      <div><Label>Press W</Label><NumInput step="0.1" value={p.pressWidth} onChange={v => setDeboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, pressWidth: v } : x) }))} /></div>
                      <div><Label>Press L</Label><NumInput step="0.1" value={p.pressLength} onChange={v => setDeboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, pressLength: v } : x) }))} /></div>
                      <div className="md:col-span-2">
                        <Label>Press</Label>
                        <select value={p.pressName || ''} onChange={e => {
                          const name = e.target.value;
                          const pressObj = presses.find(pr => pr.name === name) || null;
                          setDeboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, pressName: name, selectedPress: pressObj } : x) }));
                        }} className="w-full p-2 border rounded text-sm">
                          <option value="">Auto-select...</option>
                          {presses.map(pr => {
                            const fits = p.pressWidth && p.pressLength ? (
                              (p.pressWidth >= pr.minWidth && p.pressWidth <= pr.maxWidth && p.pressLength >= pr.minLength && p.pressLength <= pr.maxLength) ||
                              (p.pressLength >= pr.minWidth && p.pressLength <= pr.maxWidth && p.pressWidth >= pr.minLength && p.pressWidth <= pr.maxLength)
                            ) : true;
                            return <option key={pr.name} value={pr.name} disabled={!fits || pr.name.includes('Miller')}>{pr.name} ({pr.piecesPerHour}/h)</option>;
                          })}
                        </select>
                        {p.pressName && <small className="text-green-600 block">{p.pressName}</small>}
                      </div>
                      <div><Label>Auto Plate</Label><strong>${(p.platecost || 0).toFixed(2)}</strong><br />
                        <Input type="number" step="0.01" value={p.overridePlatecost ?? ''} onChange={e => {
                          const val = e.target.value;
                          setDeboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, overridePlatecost: val === '' ? null : parseFloat(val) } : x) }));
                        }} placeholder="Override" />
                      </div>
                      <div><Label>MR $</Label><NumInput step="0.01" value={p.overrideMr ?? p.mr} onChange={v => setDeboss(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, overrideMr: v === 0 ? null : v } : x) }))} /></div>
                      <div><Label>Backer $</Label>
                        <NumInput
                          step="0.01"
                          value={p.overrideBacker ?? p.backer}
                          onChange={v => setDeboss(s => ({
                            ...s,
                            plates: s.plates.map((x, j) => j === i
                              ? { ...x, overrideBacker: v == null ? null : v }
                              : x
                            )
                          }))}
                        />
                      </div>
                      {i > 0 && <button onClick={() => setDeboss(s => ({ ...s, plates: s.plates.filter((_, j) => j !== i) }))} className="text-red-600">Remove</button>}
                    </div>
                  </div>
                ))}
                <button onClick={() => setDeboss(s => ({ ...s, plates: [...s.plates, {
                  id: Date.now(), plateWidth: 0, plateLength: 0, pressWidth: 0, pressLength: 0,
                  platecost: 0, overridePlatecost: null, mr: 50, overrideMr: null, backer: 59, overrideBacker: null,
                  selectedPress: null, pressName: ''
                }] }))} className="text-red-700 font-bold text-lg">+ Add Plate</button>
              </div>
            )}
          </div>

          {/* FOIL STAMPING */}
          <div style={{marginBottom:'20px'}}>
            <label className="flex items-center gap-4 text-amber-700 text-xl font-bold">
              <input type="checkbox" checked={foil.state} onChange={e => setFoil(p => ({ ...p, state: e.target.checked }))} className="w-6 h-6" />
              Foil Stamping
            </label>
            {foil.state && (
              <div className="mt-4 p-6 border-2 border-amber-600 rounded-xl bg-amber-50">
                <div className="mb-6 p-5 bg-gradient-to-r from-amber-600 to-amber-800 text-white text-center">
                  <div className="text-xl font-bold">Plate Size = Plate Cost | Press Size = Run Cost</div>
                </div>
                {foil.plates.map((p, i) => (
                  <div key={p.id} className="mb-6 p-4 bg-white rounded-lg border">
                    <div className="grid grid-cols-2 md:grid-cols-8 gap-4 items-end">
                      <div><Label>Color</Label><Input type="text" value={p.color || ''} onChange={e => setFoil(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, color: e.target.value } : x) }))} /></div>
                      <div><Label>Plate W</Label><NumInput step="0.1" value={p.plateWidth} onChange={v => setFoil(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, plateWidth: v } : x) }))} /></div>
                      <div><Label>Plate L</Label><NumInput step="0.1" value={p.plateLength} onChange={v => setFoil(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, plateLength: v } : x) }))} /></div>
                      <div><Label>Press W</Label><NumInput step="0.1" value={p.pressWidth} onChange={v => setFoil(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, pressWidth: v } : x) }))} /></div>
                      <div><Label>Press L</Label><NumInput step="0.1" value={p.pressLength} onChange={v => setFoil(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, pressLength: v } : x) }))} /></div>
                      <div><Label>Passes</Label><NumInput min="1" value={p.passes || 1} onChange={v => setFoil(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, passes: Math.max(1, v) } : x) }))} /></div>
                      <div className="md:col-span-2">
                        <Label>Press</Label>
                        <select value={p.pressName || ''} onChange={e => {
                          const name = e.target.value;
                          const pressObj = presses.find(pr => pr.name === name) || null;
                          setFoil(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, pressName: name, selectedPress: pressObj } : x) }));
                        }} className="w-full p-2 border rounded text-sm">
                          <option value="">Auto-select...</option>
                          {presses.map(pr => {
                            const fits = p.pressWidth && p.pressLength ? (
                              (p.pressWidth >= pr.minWidth && p.pressWidth <= pr.maxWidth && p.pressLength >= pr.minLength && p.pressLength <= pr.maxLength) ||
                              (p.pressLength >= pr.minWidth && p.pressLength <= pr.maxWidth && p.pressWidth >= pr.minLength && p.pressWidth <= pr.maxLength)
                            ) : true;
                            return <option key={pr.name} value={pr.name} disabled={!fits || pr.name.includes('Miller')}>{pr.name} ({pr.piecesPerHour}/h)</option>;
                          })}
                        </select>
                        {p.pressName && <small className="text-green-600 block">{p.pressName}</small>}
                      </div>
                      <div><Label>Auto Plate</Label><strong>${(p.platecost || 0).toFixed(2)}</strong><br />
                        <Input type="number" step="0.01" value={p.overridePlatecost ?? ''} onChange={e => {
                          const val = e.target.value;
                          setFoil(s => ({ ...s, plates: s.plates.map((x, j) => j === i ? { ...x, overridePlatecost: val === '' ? null : parseFloat(val) } : x) }));
                        }} placeholder="Override" />
                      </div>
                      {i > 0 && <button onClick={() => setFoil(s => ({ ...s, plates: s.plates.filter((_, j) => j !== i) }))} className="text-red-600">Remove</button>}
                    </div>
                  </div>
                ))}
                <button onClick={() => setFoil(s => ({ ...s, plates: [...s.plates, {
                  id: Date.now(), plateWidth: 0, plateLength: 0, pressWidth: 0, pressLength: 0,
                  platecost: 0, overridePlatecost: null, color: '', passes: 1,
                  foilCost: null, overrideFoilCost: null,
                  selectedPress: null, pressName: ''
                }] }))} className="text-amber-700 font-bold text-lg">+ Add Foil Area</button>
                <div className="mt-6 flex items-center gap-4">
                  <Label width='110px'>MR per plate $</Label>
                  <NumInput value={foil.mr} onChange={v => setFoil(s => ({ ...s, mr: v }))} className="w-32" />
                </div>
              </div>
            )}
          </div>

          {/* Quantities & Pricing Cards */}
          {quantities.length > 0 && (
            <div className="mt-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-5">Quantities & Pricing</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {quantities.map(item => {
                  const qty = item.quantity;
                  const grandTotal = item.grandTotal || 0;
                  const roundedTotal = item.roundedTotal ?? Math.ceil(grandTotal);

                  return (
                    <div key={item.quantity} className="border rounded-xl p-5 bg-white shadow-lg relative" style={{padding:'10px'}}>
                      <button onClick={() => removeQuantity(item.quantity)} className="absolute top-2 right-2 bg-red-500 text-white w-7 h-7 rounded-full text-sm">X</button>
                      <div className="text-2xl font-bold text-blue-700">{item.quantity.toLocaleString()}</div>
                      <div className="text-sm text-gray-600">{item.press} • {item.hoursNeeded}h {item.isDoubleRun && '(2 passes)'}</div>

                      <div className="mt-4 space-y-1 text-sm">
                        {item.runCost > 0 && <div className="flex justify-between"><span>Machine Run:</span><strong>${item.runCost.toFixed(2)}</strong></div>}
                        {item.embossRun > 0 && <div className="flex justify-between text-purple-600"><span>Emboss Run:</span><strong>${item.embossRun.toFixed(2)}</strong></div>}
                        {item.debossRun > 0 && <div className="flex justify-between text-red-600"><span>Deboss Run:</span><strong>${item.debossRun.toFixed(2)}</strong></div>}
                        {item.foilRun > 0 && <div className="flex justify-between text-amber-700"><span>Foil Run:</span><strong>${item.foilRun.toFixed(2)}</strong></div>}
                        {item.foilMaterialCost > 0 && <div className="flex justify-between text-amber-600"><span>Foil Material Cost:</span><strong>${item.foilMaterialCost.toFixed(2)}</strong></div>}
                        {item.foilPlateAndMrCost > 0 && <div className="flex justify-between text-amber-600"><span>Foil Plates + MR:</span><strong>${item.foilPlateAndMrCost.toFixed(2)}</strong></div>}
                        {item.embossSetup > 0 && <div className="flex justify-between text-blue-600"><span>Emboss Setup:</span><strong>${item.embossSetup.toFixed(2)}</strong></div>}
                        {item.debossSetup > 0 && <div className="flex justify-between text-red-600"><span>Deboss Setup:</span><strong>${item.debossSetup.toFixed(2)}</strong></div>}
                        {item.dieTotal > 0 && <div className="flex justify-between"><span>Die:</span><strong>${item.dieTotal.toFixed(2)}</strong></div>}
                        {item.mrTotal > 0 && <div className="flex justify-between"><span>MR:</span><strong>${item.mrTotal.toFixed(2)}</strong></div>}
                        {item.additionalTotal > 0 && <div className="flex justify-between"><span>Additional Charges:</span><strong>${item.additionalTotal.toFixed(2)}</strong></div>}
                        {item.stripTotal > 0 && <div className="flex justify-between"><span>Stripping:</span><strong>${item.stripTotal.toFixed(2)}</strong></div>}
                        {item.foldingCost > 0 && <div className="flex justify-between text-indigo-600"><span>Folding:</span><strong>${item.foldingCost.toFixed(2)}</strong></div>}
                        {item.tapingCost > 0 && <div className="flex justify-between text-purple-600"><span>Taping Material:</span><strong>${item.tapingCost.toFixed(2)}</strong></div>}
                        {item.tapingRunCost > 0 && <div className="flex justify-between text-purple-700 font-medium"><span>Taping Run:</span><strong>${item.tapingRunCost.toFixed(2)}</strong></div>}
                        {item.boxesCost > 0 && <div className="flex justify-between text-orange-600 font-medium"><span>Boxes:</span><strong>${item.boxesCost.toFixed(2)}</strong></div>}
                      </div>

                      <div className="border-t-2 border-gray-300 my-3"></div>
                      <div className="text-xl font-bold text-green-600">Estimate: ${grandTotal.toFixed(2)}</div>
                      <div className="text-lg font-bold text-green-600">Total Pieces: {(qty * numUp).toLocaleString()}</div>

                      <div className="mt-2 flex items-center gap-3">
                        <span className="text-lg font-bold text-green-600 whitespace-nowrap">Total:</span>
                        <div className="flex items-center gap-1">
                          <span className="text-sm">$</span>
                          <NumInput
                            value={roundedTotal}
                            onChange={val => {
                              setQuantities(prev => prev.map(qq =>
                                qq.quantity === item.quantity
                                  ? { ...qq, roundedTotal: val }
                                  : qq
                              ));
                            }}
                            step="1"
                            min="0"
                            width="90px"
                            placeholder={Math.ceil(grandTotal).toString()}
                          />
                        </div>
                      </div>

                      <div className="text-sm text-gray-600 mt-1">${(grandTotal / qty).toFixed(4)}/unit</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mt-12 flex justify-end gap-4" style={{marginTop:'30px'}}>
            <Button onClick={handleSubmit} disabled={quantities.length === 0} className="px-10 py-4 text-xl font-bold bg-blue-600 hover:bg-blue-700 text-white">
              {editingItem ? 'Update Item' : 'Add to Estimate'}
            </Button>
            <Button onClick={closeMini} variant="secondary" className="px-8 py-4 text-xl">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Necc;